// src/iap/initPurchases.ts
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import * as appActions from '../Actions/app';
import { reportError } from 'Utils/reportError';
import { appStoreAccountToken } from './accountToken';
import { verificationFailureNotice } from './verificationFailureNotice';
declare global {
  interface Window {
    CdvPurchase?: CdvPurchaseApi;
    cordova?: { exec?: CordovaExec };
    Capacitor: unknown;
    __iapInitialized?: boolean;
    __iapSetupPromise?: Promise<CdvPurchaseApi | undefined>;
    __iapLastVerifiedReceiptHash?: string;
    __iapLastVerifiedReceiptActive?: boolean;
    __iapLastVerifiedReceiptAt?: number;
    __iapVerifyArmedUntil?: number;
    __iapVerifyArmedProductId?: string;
    __iapLastProductLoadErrors?: CdvStoreError[];
  }
}

type MinimalUser = {
  id?: string | number;
  user_id?: string;
  email?: string | null;
  subscription_provider?: string | null;
} | null;
type Dispatch = (action: ReturnType<typeof appActions.setUserData>) => void;
const accountKey = (user: MinimalUser) => user?.user_id ?? user?.id ?? user?.email;
let iapAccountRevision = 0;
// One verification notice per store action (opening the plans, buying, Restore purchases): a store
// update and a restore can both hand over the same refused purchase.
let verificationNoticeShown = false;
let currentIAPContext:
  | {
      userData: MinimalUser;
      dispatch: Dispatch;
      currentAuthToken?: () => Promise<string | undefined>;
    }
  | undefined;

type CordovaExec = (
  success: (res: unknown) => void,
  error: (e: unknown) => void,
  service: string,
  action: string,
  args: unknown[],
) => void;

export type CdvStoreError = {
  isError?: boolean;
  code?: string | number;
  message?: string;
  productId?: string | null;
};

export type CdvPricingPhase = {
  price: string;
  priceMicros: number;
  currency: string;
  billingPeriod?: string;
  recurrenceMode?: string;
};

export type CdvPurchaseOffer = {
  id?: string;
  pricingPhases?: CdvPricingPhase[];
  productId: string;
  platform: unknown;
  order?: (additionalData?: unknown) => Promise<CdvStoreError | undefined>;
  canPurchase?: boolean;
};

export type CdvPurchaseProduct = {
  id: string;
  canPurchase?: boolean;
  getOffer?: (id?: string) => CdvPurchaseOffer | undefined;
  offers?: CdvPurchaseOffer[];
};

/** `state` is one of the plugin's TransactionState values ('initiated', 'approved', ...). */
export type CdvLocalTransaction = { state?: string };

export type CdvPurchaseStore = {
  verbosity?: unknown;
  minTimeBetweenUpdates?: number;
  applicationUsername?: () => string;
  obfuscator?: 'legacy' | 'uuid' | 'disabled';
  register: (products: unknown[]) => void;
  when: () => { approved: (cb: (tx: CdvPurchaseTransaction) => void | Promise<void>) => void };
  error: (cb: (e: unknown) => void) => void;
  ready: (cb: () => void) => void;
  initialize: (platforms: unknown[]) => Promise<unknown>;
  update: () => Promise<unknown>;
  get: (id: string, platform?: unknown) => CdvPurchaseProduct | undefined;
  /** The transaction the store's canPurchase check reads for this product on this device. */
  findInLocalReceipts?: (product: CdvPurchaseProduct) => CdvLocalTransaction | undefined;
  getReceipt?: (platform: unknown) => Promise<unknown>;
  restorePurchases?: () => Promise<CdvStoreError | undefined>;
  order: (offer: CdvPurchaseOffer, additionalData?: unknown) => Promise<CdvStoreError | undefined>;
};

export type CdvPurchaseApi = {
  store: CdvPurchaseStore;
  ProductType: { PAID_SUBSCRIPTION: unknown };
  Platform: { APPLE_APPSTORE: unknown; GOOGLE_PLAY: unknown };
  LogLevel?: { DEBUG: unknown };
  ErrorCode?: { PAYMENT_CANCELLED: string | number };
};

type GooglePlayNativePurchase = {
  acknowledged?: boolean;
  orderId?: string;
  packageName?: string;
  productId?: string;
  productIds?: string[];
  purchaseTime?: number;
  purchaseToken?: string;
  receipt?: string;
  signature?: string;
};

type CdvPurchaseTransaction = {
  products?: { id: string }[];
  receipt?: unknown;
  nativePurchase?: GooglePlayNativePurchase;
  parentReceipt?: unknown;
  transactionId?: string;
  originalTransactionId?: string;
  platform?: unknown;
  finish: () => void | Promise<void>;
};

export const IAP_VERIFICATION_EVENT = 'museflow:iap-verification';

const LOG = '[IAP]';
const log = (...a: unknown[]) => console.log(LOG, ...a);
const warn = (...a: unknown[]) => console.warn(LOG, ...a);
const err = (...a: unknown[]) => console.error(LOG, ...a);

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
const IAP_VERIFY_ARM_WINDOW_MS = 15 * 60 * 1000;
const IAP_PLUGIN_WAIT_TIMEOUT_MS = 10 * 1000;
const IAP_PLUGIN_WAIT_INTERVAL_MS = 100;
const CORDOVA_DEVICE_READY_TIMEOUT_MS = 10 * 1000;

export const LEGACY_APPLE_IAP_PRODUCT_IDS = [
  'com.museflow.monthly',
  'com.museflow.3months',
  'com.museflow.annual',
] as const;

export const APPLE_IAP_PRODUCT_IDS = [
  'com.museflow.monthly.v2',
  'com.museflow.3months.v2',
  'com.museflow.annual.v2',
] as const;

export const ANDROID_IAP_PRODUCT_IDS = [
  process.env.REACT_APP_ANDROID_IAP_MONTHLY_PRODUCT_ID || 'com.museflow.monthly',
  process.env.REACT_APP_ANDROID_IAP_3_MONTH_PRODUCT_ID || 'com.museflow.3months',
  process.env.REACT_APP_ANDROID_IAP_ANNUAL_PRODUCT_ID || 'com.museflow.annual',
] as const;

export type NativeIAPStorePlatform = 'apple' | 'google';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isStoreError = (value: unknown): value is CdvStoreError => {
  const error = value as CdvStoreError;
  return (
    !!error && typeof error === 'object' && (!!error.message || !!error.code || !!error.isError)
  );
};

function storeErrorsFrom(result: unknown): CdvStoreError[] {
  if (Array.isArray(result)) return result.filter(isStoreError);
  return isStoreError(result) ? [result] : [];
}

function getCdvPurchase(): CdvPurchaseApi | undefined {
  const Cdv = window.CdvPurchase;
  if (
    Cdv?.store &&
    Cdv?.Platform?.APPLE_APPSTORE &&
    Cdv?.Platform?.GOOGLE_PLAY &&
    Cdv?.ProductType?.PAID_SUBSCRIPTION
  ) {
    return Cdv;
  }
  return undefined;
}

export function nativeIAPStorePlatform(): NativeIAPStorePlatform | undefined {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'apple';
  if (platform === 'android') return 'google';
  return undefined;
}

export function iapStoreDisplayName(platform: NativeIAPStorePlatform) {
  return platform === 'apple' ? 'Apple' : 'Google Play';
}

function nativeStorePlatform(Cdv: CdvPurchaseApi, platform: NativeIAPStorePlatform) {
  return platform === 'apple' ? Cdv.Platform.APPLE_APPSTORE : Cdv.Platform.GOOGLE_PLAY;
}

function iapProductIds(platform: NativeIAPStorePlatform) {
  return platform === 'apple' ? APPLE_IAP_PRODUCT_IDS : ANDROID_IAP_PRODUCT_IDS;
}

async function waitForCdvPurchase(
  timeoutMs: number = IAP_PLUGIN_WAIT_TIMEOUT_MS,
): Promise<CdvPurchaseApi | undefined> {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const Cdv = getCdvPurchase();
    if (Cdv) return Cdv;
    await delay(IAP_PLUGIN_WAIT_INTERVAL_MS);
  }
  return getCdvPurchase();
}

export function waitForDeviceReady(timeoutMs: number = CORDOVA_DEVICE_READY_TIMEOUT_MS) {
  if (!window.cordova) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let settled = false;
    const timeout = { id: undefined as number | undefined };
    const finish = () => {
      if (settled) return;
      settled = true;
      document.removeEventListener('deviceready', finish);
      if (timeout.id !== undefined) window.clearTimeout(timeout.id);
      resolve();
    };

    // Cordova's deviceready event is sticky: when it has already fired,
    // addEventListener invokes the listener synchronously. Define the timer
    // binding first and avoid scheduling one after that immediate resolution.
    document.addEventListener('deviceready', finish, { once: true });
    if (settled) return;

    timeout.id = window.setTimeout(() => {
      if (!settled) {
        reportError(
          new Error(`'deviceready' not fired within ${timeoutMs}ms; proceeding without it`),
          'iap.deviceReady',
        );
      }
      finish();
    }, timeoutMs);
  });
}

export function armIAPReceiptVerification(productId: string) {
  const expiresAt = Date.now() + IAP_VERIFY_ARM_WINDOW_MS;
  window.__iapVerifyArmedUntil = expiresAt;
  window.__iapVerifyArmedProductId = productId;
  log('verify arm set for product', productId, 'until', new Date(expiresAt).toISOString());
}

export function clearIAPReceiptVerificationArm() {
  window.__iapVerifyArmedUntil = undefined;
  window.__iapVerifyArmedProductId = undefined;
}

function isReceiptVerificationArmed(productIds: string[]): boolean {
  const armedUntil = window.__iapVerifyArmedUntil ?? 0;
  if (!armedUntil) return false;

  if (Date.now() > armedUntil) {
    log('verify arm expired, clearing arm');
    clearIAPReceiptVerificationArm();
    return false;
  }

  const armedProductId = window.__iapVerifyArmedProductId;
  if (armedProductId && productIds.length > 0 && !productIds.includes(armedProductId)) {
    log('verify arm mismatch, skipping verify', {
      armedProductId,
      approvedProductIds: productIds,
    });
    return false;
  }

  return true;
}

const looksB64 = (s: unknown): s is string =>
  typeof s === 'string' && s.length > 200 && /^[A-Za-z0-9+/=]+$/.test(s);

function pickB64(obj: unknown): string | undefined {
  if (!obj) return;
  if (looksB64(obj)) return obj;
  const keys = [
    'appStoreReceipt',
    'appStoreReceiptBase64',
    'latestReceipt',
    'receipt',
    'receipt_data',
    'base64',
    'data',
    'raw',
  ];
  const o = obj as Record<string, unknown>;
  for (const k of keys) if (looksB64(o?.[k])) return o[k] as string;
  for (const v of Object.values(o)) if (looksB64(v)) return v as string;
  return;
}

// Tiny cheap hash for dedupe (NOT cryptographic; just for “don’t spam verify”).
function cheapHash(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  // unsigned
  return (h >>> 0).toString(16);
}

function getAppleAppReceiptViaExec(): Promise<string | undefined> {
  return new Promise((resolve) => {
    const exec = window?.cordova?.exec;
    if (!exec) {
      warn('cordova.exec not available');
      return resolve(undefined);
    }
    log('exec → InAppPurchase.appStoreReceipt');
    exec(
      (res: unknown) => {
        const b64 = pickB64(res);
        log('exec ← appStoreReceipt:', b64 ? `ok(len=${b64.length})` : `no-b64`);
        resolve(b64);
      },
      (e: unknown) => {
        warn('exec appStoreReceipt failed', e);
        resolve(undefined);
      },
      'InAppPurchase',
      'appStoreReceipt',
      [],
    );
  });
}

async function getAppleReceiptB64(Cdv: CdvPurchaseApi): Promise<string | undefined> {
  const { store, Platform } = Cdv;
  // Preferred: v13+ getter
  if (typeof store?.getReceipt === 'function') {
    try {
      const r = await store.getReceipt(Platform.APPLE_APPSTORE);
      const b64 = pickB64(r);
      log('store.getReceipt →', b64 ? `ok(len=${b64.length})` : 'no-b64');
      if (b64) return b64;
    } catch (e) {
      warn('store.getReceipt threw', e);
    }
  } else {
    log('store.getReceipt is undefined (JS bundled incorrectly or old adapter).');
  }
  // Fallback: call native directly
  return getAppleAppReceiptViaExec();
}

type IAPVerifyPayload =
  | {
      platform: 'apple';
      receipt_data: string;
      reason?: 'approved';
      tx_id?: string;
      original_tx_id?: string;
    }
  | {
      platform: 'google';
      product_id: string;
      product_ids: string[];
      purchase_token: string;
      package_name?: string;
      order_id?: string;
      receipt_data?: string;
      signature?: string;
      reason?: 'approved';
      tx_id?: string;
      purchase_time?: number;
      acknowledged?: boolean;
    };

type GooglePurchasePayload = Extract<IAPVerifyPayload, { platform: 'google' }>;

function verifyPayloadDedupeKey(payload: IAPVerifyPayload) {
  if (payload.platform === 'apple') return `apple:${cheapHash(payload.receipt_data)}`;
  return `google:${payload.purchase_token || cheapHash(JSON.stringify(payload))}`;
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;

const readString = (source: Record<string, unknown> | undefined, key: string) => {
  const value = source?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const readNumber = (source: Record<string, unknown> | undefined, key: string) => {
  const value = source?.[key];
  return typeof value === 'number' ? value : undefined;
};

const readBoolean = (source: Record<string, unknown> | undefined, key: string) => {
  const value = source?.[key];
  return typeof value === 'boolean' ? value : undefined;
};

const readStringArray = (source: Record<string, unknown> | undefined, key: string) => {
  const value = source?.[key];
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
};

function googlePurchasePayloadFromTransaction(
  tx: CdvPurchaseTransaction,
): GooglePurchasePayload | undefined {
  const nativePurchase = asRecord(tx.nativePurchase);
  const parentReceipt = asRecord(tx.parentReceipt);
  const receipt = asRecord(tx.receipt);
  const productIds = [
    ...readStringArray(nativePurchase, 'productIds'),
    ...((tx.products || []).map((p) => p.id).filter(Boolean) as string[]),
  ];
  const productId =
    productIds[0] || readString(nativePurchase, 'productId') || (tx.products || [])[0]?.id;
  const purchaseToken =
    readString(nativePurchase, 'purchaseToken') ||
    readString(parentReceipt, 'purchaseToken') ||
    readString(receipt, 'purchaseToken');

  if (!productId || !purchaseToken) return undefined;

  return {
    platform: 'google',
    product_id: productId,
    product_ids: Array.from(new Set(productIds.length > 0 ? productIds : [productId])),
    purchase_token: purchaseToken,
    package_name: readString(nativePurchase, 'packageName') || readString(receipt, 'packageName'),
    order_id:
      readString(nativePurchase, 'orderId') ||
      readString(parentReceipt, 'orderId') ||
      readString(receipt, 'orderId'),
    receipt_data: readString(nativePurchase, 'receipt') || readString(receipt, 'receipt'),
    signature: readString(nativePurchase, 'signature') || readString(receipt, 'signature'),
    reason: 'approved',
    tx_id: tx.transactionId,
    purchase_time:
      readNumber(nativePurchase, 'purchaseTime') ?? readNumber(receipt, 'purchaseTime'),
    acknowledged:
      readBoolean(nativePurchase, 'acknowledged') ?? readBoolean(receipt, 'acknowledged'),
  };
}

async function verify(
  payload: IAPVerifyPayload,
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
): Promise<boolean> {
  const accountRevision = iapAccountRevision;
  if (!currentIAPContext || accountKey(userData) !== accountKey(currentIAPContext.userData))
    return false;
  try {
    const token = (await currentAuthToken?.()) || '';
    if (!token || accountRevision !== iapAccountRevision) {
      warn('verify skipped: missing or changed account');
      return false;
    }

    // Dedupe per receipt blob / Google purchase token.
    const h = `${userData?.id ?? userData?.user_id ?? userData?.email ?? ''}:${verifyPayloadDedupeKey(payload)}`;
    if (
      window.__iapLastVerifiedReceiptHash === h &&
      Date.now() - (window.__iapLastVerifiedReceiptAt ?? 0) < 5000
    ) {
      log('verify skipped: same receipt hash already verified');
      return !!window.__iapLastVerifiedReceiptActive;
    }

    const url = `${baseUrl}${payload.platform === 'google' ? 'google/verify-purchase' : 'receipts/verify'}`;
    const request =
      payload.platform === 'google'
        ? { purchaseToken: payload.purchase_token, productId: payload.product_id }
        : payload;
    const { data, status } = await axios.post(url, request, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 15000,
    });
    if (accountRevision !== iapAccountRevision) return false;

    log(
      'verify ←',
      status,
      'platform=',
      payload.platform,
      'active=',
      !!data?.active,
      'env=',
      data?.environment,
    );
    window.__iapLastVerifiedReceiptHash = h;
    window.__iapLastVerifiedReceiptAt = Date.now();
    window.__iapLastVerifiedReceiptActive = !!data?.active;

    if (data?.active) {
      dispatch(
        appActions.setUserData({
          ...(userData || {}),
          subscription_status: data.subscriptionStatus || 'active',
          subscription_provider: payload.platform,
        }),
      );
      return true;
    } else {
      log('verify returned inactive receipt; skipping local subscription mutation');
      return false;
    }
  } catch (e: unknown) {
    const errObj = e as { response?: { status?: number }; message?: string };
    err('verify failed', errObj?.response?.status, errObj?.message);
    reportError(e, 'iap.verify');
    if (!verificationNoticeShown) {
      verificationNoticeShown = true;
      window.dispatchEvent(
        new CustomEvent(IAP_VERIFICATION_EVENT, {
          detail: verificationFailureNotice(e, payload.platform),
        }),
      );
    }
    return false;
  }
}

/** In-flight receipt callbacks must not restore a signed-out or replaced account. */
export function resetIAPAccount() {
  iapAccountRevision += 1;
  currentIAPContext = undefined;
  window.__iapLastVerifiedReceiptHash = undefined;
  window.__iapLastVerifiedReceiptActive = undefined;
  clearIAPReceiptVerificationArm();
}

export function setupIAP(
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
) {
  const previousUser = currentIAPContext?.userData;
  if (accountKey(previousUser ?? null) !== accountKey(userData)) {
    resetIAPAccount();
  }
  currentIAPContext = { userData, dispatch, currentAuthToken };
  const platform = nativeIAPStorePlatform();
  if (!platform) {
    warn('setupIAP skipped: no native store platform for', Capacitor.getPlatform());
    return Promise.resolve(undefined);
  }

  if (window.__iapInitialized) {
    log('already initialized');
    return Promise.resolve(getCdvPurchase());
  }

  if (window.__iapSetupPromise) {
    log('initialization already in progress');
    return window.__iapSetupPromise;
  }

  window.__iapSetupPromise = (async () => {
    const Cdv = await waitForCdvPurchase();
    if (!Cdv) {
      err('CdvPurchase.store missing after waiting for Cordova purchase plugin');
      reportError(
        new Error(
          `CdvPurchase.store missing after waiting ${IAP_PLUGIN_WAIT_TIMEOUT_MS}ms for Cordova purchase plugin`,
        ),
        'iap.pluginWait',
      );
      return undefined;
    }

    await waitForDeviceReady();

    const { store, ProductType, LogLevel } = Cdv;
    const storePlatform = nativeStorePlatform(Cdv, platform);
    const productIds =
      platform === 'apple'
        ? [...APPLE_IAP_PRODUCT_IDS, ...LEGACY_APPLE_IAP_PRODUCT_IDS]
        : ANDROID_IAP_PRODUCT_IDS;
    try {
      store.verbosity = LogLevel?.DEBUG;
    } catch {
      // intentionally ignore
    }

    // An App Store purchase carries the account's user id, so Apple's server
    // notifications for it can be matched to the account even when this app
    // never gets to report the purchase itself. Google Play purchases get
    // their account id from the server at order time and are left as they were.
    store.applicationUsername = () =>
      platform === 'apple'
        ? (appStoreAccountToken(currentIAPContext?.userData) ?? '')
        : (currentIAPContext?.userData?.id ??
            currentIAPContext?.userData?.user_id ??
            currentIAPContext?.userData?.email ??
            '') + '';
    // Hand that value to the store exactly as it is. The plugin's other modes
    // replace it with a hash, which would no longer be the account's user id.
    if (platform === 'apple') store.obfuscator = 'disabled';

    store.register(
      productIds.map((id) => ({
        id,
        type: ProductType.PAID_SUBSCRIPTION,
        platform: storePlatform,
        group: 'museflow',
      })),
    );

    store.when().approved(async (tx: CdvPurchaseTransaction) => {
      const context = currentIAPContext;
      if (!context) return;
      const txProductIds = (tx?.products || []).map((p) => p.id);
      let shouldFinishTransaction = false;
      log('approved:', {
        txId: tx?.transactionId,
        orig: tx?.originalTransactionId,
        plat: tx?.platform,
        products: txProductIds,
      });

      try {
        const verificationArmed = isReceiptVerificationArmed(txProductIds);
        if (!verificationArmed && platform === 'apple') {
          log('approved: skipping verify (not armed by user purchase action)');
          // Keep delayed approvals available for an explicit restore on the right account.
        } else {
          if (!verificationArmed) {
            log('approved: verifying Google Play purchase surfaced by store update');
          }
          let verifiedActive = false;

          if (platform === 'apple') {
            // Prefer receipt attached to tx, else pull app receipt.
            let b64 = pickB64(tx?.receipt);
            log('approved: tx.receipt present?', !!b64);
            if (!b64) b64 = await getAppleReceiptB64(Cdv);

            if (b64) {
              verifiedActive = await verify(
                {
                  platform: 'apple',
                  receipt_data: b64,
                  reason: 'approved',
                  tx_id: tx?.transactionId,
                  original_tx_id: tx?.originalTransactionId,
                },
                context.userData,
                context.dispatch,
                context.currentAuthToken,
              );
            } else {
              warn('approved: no app receipt available to verify');
            }
          } else {
            const googlePayload = googlePurchasePayloadFromTransaction(tx);
            if (googlePayload) {
              verifiedActive = await verify(
                googlePayload,
                context.userData,
                context.dispatch,
                context.currentAuthToken,
              );
            } else {
              warn('approved: no Google purchase token available to verify');
            }
          }

          shouldFinishTransaction = verifiedActive;
          if (verifiedActive) clearIAPReceiptVerificationArm();
        }
      } finally {
        if (shouldFinishTransaction) {
          try {
            await tx.finish();
            log('approved: finish()');
          } catch (e) {
            err('finish() failed', e);
          }
        } else {
          warn('approved: finish() skipped until server verification succeeds');
        }
        try {
          if (shouldFinishTransaction) {
            await store.update();
            log('approved: store.update()');
          }
        } catch {
          // intentionally ignore
        }
      }
    });

    store.error((e: unknown) => err('Store Error:', e));
    store.ready(() => log('Store ready'));
    window.__iapInitialized = true;

    try {
      log(`initialize([${iapStoreDisplayName(platform)}])`);
      const initializeResult = await store.initialize([storePlatform]);
      const initializeErrors = storeErrorsFrom(initializeResult);
      if (initializeErrors.length > 0) {
        window.__iapLastProductLoadErrors = initializeErrors;
        warn('initialize returned product load errors', initializeErrors);
      }
      log('force product refresh after initialize');
      await forceIAPProductRefresh(Cdv);

      // IMPORTANT: removed cold-start verification
      // We only verify on actual approved transactions.
      log('startup: skipping verify (only verifying on purchase approval)');
    } catch (e) {
      err('initialize/update failed', e);
    }

    return Cdv;
  })().finally(() => {
    window.__iapSetupPromise = undefined;
  });

  return window.__iapSetupPromise;
}

async function withStoreTimeout<T>(operation: Promise<T>, timeoutMs = 20000): Promise<T> {
  let timeout: number | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_resolve, reject) => {
        timeout = window.setTimeout(
          () => reject(new Error('The store is taking too long to respond. Please try again.')),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    if (timeout !== undefined) window.clearTimeout(timeout);
  }
}

export async function ensureIAPReady(
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
): Promise<CdvPurchaseApi | undefined> {
  verificationNoticeShown = false;
  const setup = setupIAP(userData, dispatch, currentAuthToken);
  const revision = iapAccountRevision;
  const Cdv = await withStoreTimeout(setup);
  if (revision !== iapAccountRevision) throw new Error('Your account changed. Please retry.');
  if (!Cdv) return undefined;
  await withStoreTimeout(forceIAPProductRefresh(Cdv));
  if (revision !== iapAccountRevision) throw new Error('Your account changed. Please retry.');
  return Cdv;
}

export function appleProductIdForPlan(planSelect: string) {
  return iapProductIdForPlan(planSelect, 'apple');
}

export function iapProductIdForPlan(
  planSelect: string,
  platform: NativeIAPStorePlatform = nativeIAPStorePlatform() || 'apple',
) {
  const productIds = iapProductIds(platform);
  return planSelect === 'monthly'
    ? productIds[0]
    : planSelect === '3-months'
      ? productIds[1]
      : planSelect === 'annual'
        ? productIds[2]
        : '';
}

export function appleProductStates(Cdv: CdvPurchaseApi) {
  return iapProductStates(Cdv, 'apple');
}

export function iapProductStates(
  Cdv: CdvPurchaseApi,
  platform: NativeIAPStorePlatform = nativeIAPStorePlatform() || 'apple',
) {
  const { store } = Cdv;
  const storePlatform = nativeStorePlatform(Cdv, platform);
  return iapProductIds(platform).map((id) => {
    const product = store.get(id, storePlatform);
    return {
      id,
      returned: !!product,
      canPurchase: !!product?.canPurchase,
    };
  });
}

export async function forceIAPProductRefresh(Cdv: CdvPurchaseApi) {
  const store = Cdv.store as CdvPurchaseStore & { lastUpdate?: number };
  const previousMinTimeBetweenUpdates = store.minTimeBetweenUpdates;

  try {
    store.minTimeBetweenUpdates = 0;
    store.lastUpdate = 0;
    const updateResult = await store.update();
    const updateErrors = storeErrorsFrom(updateResult);
    if (updateErrors.length > 0) {
      window.__iapLastProductLoadErrors = updateErrors;
      warn('product refresh returned errors', updateErrors);
    }
  } finally {
    store.minTimeBetweenUpdates = previousMinTimeBetweenUpdates;
  }
}

/** Restore remains available even when the acquisition catalog is unavailable. */
export async function restoreIAPPurchases(
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
): Promise<boolean> {
  const Cdv = await ensureIAPReady(userData, dispatch, currentAuthToken);
  const accountRevision = iapAccountRevision;
  if (!Cdv?.store.restorePurchases)
    throw new Error('Restore purchases is unavailable. Please try again.');
  window.__iapLastVerifiedReceiptHash = undefined;
  window.__iapLastVerifiedReceiptActive = undefined;
  window.__iapVerifyArmedUntil = Date.now() + IAP_VERIFY_ARM_WINDOW_MS;
  window.__iapVerifyArmedProductId = undefined;
  const error = await withStoreTimeout(Cdv.store.restorePurchases());
  if (accountRevision !== iapAccountRevision) return false;
  if (error) {
    clearIAPReceiptVerificationArm();
    throw new Error(error.message || 'Unable to restore purchases. Please try again.');
  }
  if (nativeIAPStorePlatform() === 'apple') {
    const receipt = await getAppleReceiptB64(Cdv);
    if (accountRevision !== iapAccountRevision) return false;
    clearIAPReceiptVerificationArm();
    if (!receipt) return false;
    return verify(
      { platform: 'apple', receipt_data: receipt },
      userData,
      dispatch,
      currentAuthToken,
    );
  }
  // Play approval callbacks verify and acknowledge restored transactions; also reconcile
  // the already-linked subscription when there is no new local transaction to approve.
  if (userData?.subscription_provider === 'google') {
    const token = await currentAuthToken?.();
    if (accountRevision !== iapAccountRevision) return false;
    const { data } = await axios.post(
      `${baseUrl}google/refresh`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      },
    );
    if (accountRevision !== iapAccountRevision) return false;
    if (data?.subscriptionStatus) {
      dispatch(
        appActions.setUserData({
          ...(userData || {}),
          subscription_status: data.subscriptionStatus,
        }),
      );
    }
    return !!data?.active;
  }
  return false;
}
